import React, { useState, useRef } from "react";

// Single large photo preview with blurred color-matched fill and a real
// sliding track. One finger swipes between photos; two fingers pinch-zoom the
// current photo (pan while zoomed). Single tap opens the full lightbox.
export default function PhotoCarousel({ photos, isMobile, onOpen }) {
  const [index, setIndex] = useState(0);
  const [dragX, setDragX] = useState(0);     // live finger offset in px (swipe)
  const [dragging, setDragging] = useState(false);
  const [zoom, setZoom] = useState({ scale: 1, x: 0, y: 0 }); // inline pinch zoom of current photo
  const touchX = useRef(null);
  const frameRef = useRef(null);
  const g = useRef({ mode: null, startDist: 0, startScale: 1, startMid: null, startXY: { x: 0, y: 0 }, moved: false });
  const n = photos.length;

  const clamp = (i) => Math.max(0, Math.min(n - 1, i));
  const go = (dir) => { setZoom({ scale: 1, x: 0, y: 0 }); setIndex((i) => clamp(i + dir)); };

  const frameAspect = isMobile ? "4 / 5" : "3 / 2";

  const dist = (t) => Math.hypot(t[0].clientX - t[1].clientX, t[0].clientY - t[1].clientY);
  const mid = (t) => ({ x: (t[0].clientX + t[1].clientX) / 2, y: (t[0].clientY + t[1].clientY) / 2 });

  const onTouchStart = (e) => {
    g.current.moved = false;
    if (e.touches.length === 2) {
      // begin pinch
      g.current.mode = "pinch";
      g.current.opened = false;
      g.current.startDist = dist(e.touches);
      g.current.startScale = zoom.scale;
      g.current.startMid = mid(e.touches);
      g.current.startXY = { x: zoom.x, y: zoom.y };
    } else {
      // begin swipe or pan (if zoomed)
      g.current.mode = zoom.scale > 1 ? "pan" : "swipe";
      touchX.current = e.touches[0].clientX;
      g.current.startXY = { x: zoom.x, y: zoom.y };
      g.current.panStart = { x: e.touches[0].clientX, y: e.touches[0].clientY };
      if (g.current.mode === "swipe") setDragging(true);
    }
  };

  const onTouchMove = (e) => {
    g.current.moved = true;
    if (g.current.mode === "pinch" && e.touches.length === 2) {
      const ratio = dist(e.touches) / (g.current.startDist || 1);
      // A pinch gesture jumps straight to the full-screen viewer (which has
      // proper pinch-zoom, double-tap, and pan) rather than the limited inline zoom.
      if (ratio > 1.12 && onOpen && !g.current.opened) {
        g.current.opened = true;
        onOpen(index);
      }
      return;
    } else if (g.current.mode === "pan" && zoom.scale > 1) {
      const dx = e.touches[0].clientX - g.current.panStart.x;
      const dy = e.touches[0].clientY - g.current.panStart.y;
      const max = (zoom.scale - 1) * 160;
      setZoom((z) => ({ ...z, x: Math.max(-max, Math.min(max, g.current.startXY.x + dx)), y: Math.max(-max, Math.min(max, g.current.startXY.y + dy)) }));
    } else if (g.current.mode === "swipe") {
      if (touchX.current != null) setDragX(e.touches[0].clientX - touchX.current);
    }
  };

  const onTouchEnd = (e) => {
    if (g.current.mode === "swipe") {
      const w = frameRef.current ? frameRef.current.offsetWidth : 1;
      const threshold = w * 0.18;
      if (dragX < -threshold) go(1);
      else if (dragX > threshold) go(-1);
      setDragX(0);
      setDragging(false);
    } else if (g.current.mode === "pinch") {
      // snap back to 1 if barely zoomed
      if (zoom.scale < 1.1) setZoom({ scale: 1, x: 0, y: 0 });
    }
    touchX.current = null;
    g.current.mode = null;
  };

  return (
    <div style={{ marginTop: isMobile ? 14 : 16 }}>
      <div
        ref={frameRef}
        style={{ position: "relative", width: "100%", aspectRatio: frameAspect, borderRadius: 0, overflow: "hidden", borderTop: "1px solid var(--border)", borderBottom: "1px solid var(--border)", background: "#0B1626", touchAction: zoom.scale > 1 ? "none" : "pan-y" }}
        onTouchStart={onTouchStart}
        onTouchMove={onTouchMove}
        onTouchEnd={onTouchEnd}
      >
        {/* Blurred color-matched fill for the current photo */}
        <div style={{ position: "absolute", inset: 0, backgroundImage: `url(${photos[index]})`, backgroundSize: "cover", backgroundPosition: "center", filter: "blur(28px) brightness(0.8)", transform: "scale(1.2)", transition: "background-image 0.3s ease" }} />

        {/* Sliding track */}
        <div
          style={{
            position: "absolute", inset: 0, display: "flex",
            transform: `translateX(calc(${-index * 100}% + ${dragX}px))`,
            transition: dragging ? "none" : "transform 0.4s cubic-bezier(0.22, 0.61, 0.36, 1)",
          }}
        >
          {photos.map((u, i) => (
            <div key={i} style={{ position: "relative", flex: "0 0 100%", width: "100%", height: "100%", display: "flex", alignItems: "center", justifyContent: "center", overflow: "hidden" }}>
              <img
                src={u}
                alt={`Race photo ${i + 1}`}
                loading="lazy"
                decoding="async"
                onClick={() => { if (!g.current.moved && zoom.scale === 1) onOpen(i); }}
                onDoubleClick={() => { if (i === index) setZoom((z) => z.scale > 1 ? { scale: 1, x: 0, y: 0 } : { scale: 2, x: 0, y: 0 }); }}
                draggable={false}
                style={{
                  width: "100%", height: "100%", objectFit: "contain", display: "block", userSelect: "none",
                  cursor: zoom.scale > 1 ? "grab" : "zoom-in",
                  transform: i === index ? `translate(${zoom.x}px, ${zoom.y}px) scale(${zoom.scale})` : "none",
                  transition: g.current.mode ? "none" : "transform 0.25s ease",
                }}
              />
            </div>
          ))}
        </div>
      </div>

      {/* Dots */}
      {n > 1 && (
        <div style={{ display: "flex", justifyContent: "center", gap: 6, marginTop: 10 }}>
          {photos.map((_, i) => (
            <button
              key={i}
              onClick={() => { setZoom({ scale: 1, x: 0, y: 0 }); setIndex(i); }}
              aria-label={`Photo ${i + 1}`}
              style={{ width: i === index ? 18 : 7, height: 7, borderRadius: 999, border: "none", padding: 0, cursor: "pointer", background: i === index ? "var(--navy)" : "var(--border)", transition: "width 0.3s ease, background 0.3s ease" }}
            />
          ))}
        </div>
      )}
    </div>
  );
}

